import { ExternalLink, Play } from "lucide-react";

import { SourceOperationsSheet } from "@/components/sources/source-operations-sheet";
import { SourcePauseMenu } from "@/components/sources/source-pause-menu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { SourceConfig } from "@/lib/api";

interface Props {
  source: SourceConfig;
  onRun: (sourceId: string) => void;
  running?: boolean;
}

/**
 * One source = one card. Surfaces everything an operator would want at a
 * glance: routing (persist/ingest/both), tier, cadence, active/inactive,
 * and a Run-now button for scrapers.
 */
export function SourceCard({ source, onRun, running }: Props) {
  const isScraper = source.source_type === "scraper";
  const isActive = source.is_active && source.deactivated_at === null;

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="truncate">{source.display_name}</CardTitle>
            <CardDescription className="font-mono">{source.id}</CardDescription>
          </div>
          <ActiveDot active={isActive} />
        </div>
      </CardHeader>

      <CardContent className="flex-1 space-y-3 text-sm">
        {source.description ? (
          <p className="text-xs text-muted-fg line-clamp-2">
            {source.description}
          </p>
        ) : null}

        <div className="flex flex-wrap gap-1.5">
          {source.source_type ? (
            <Badge variant="secondary">{source.source_type}</Badge>
          ) : null}
          <Badge variant={routingVariant(source.routing)}>
            {source.routing}
          </Badge>
          <Badge variant={tierVariant(source.confidence_tier)}>
            {source.confidence_tier}
          </Badge>
          <Badge variant="outline">{source.cadence}</Badge>
        </div>

        {source.source_url ? (
          <a
            href={source.source_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
          >
            <span className="truncate">{source.source_url}</span>
            <ExternalLink className="size-3 shrink-0" />
          </a>
        ) : null}
      </CardContent>

      <CardFooter>
        {isScraper ? (
          <div className="ml-auto flex items-center gap-2">
            <SourceOperationsSheet source={source} />
            <SourcePauseMenu source={source} />
            <Button
              size="sm"
              variant="outline"
              onClick={() => onRun(source.id)}
              disabled={!isActive || running || source.is_schedule_paused}
              title={
                source.is_schedule_paused
                  ? "Resume the source to run it manually"
                  : undefined
              }
            >
              <Play />
              {running ? "Running…" : "Run now"}
            </Button>
          </div>
        ) : (
          <span className="ml-auto text-xs text-muted-fg">
            {source.source_type === "webhook" ? "webhook-driven" : "no action"}
          </span>
        )}
      </CardFooter>
    </Card>
  );
}

function ActiveDot({ active }: { active: boolean }) {
  return (
    <span
      className={
        active
          ? "mt-1.5 size-2 shrink-0 rounded-full bg-accent shadow-[0_0_0_3px_rgba(34,197,94,0.15)]"
          : "mt-1.5 size-2 shrink-0 rounded-full bg-muted-fg/50"
      }
      aria-label={active ? "active" : "inactive"}
      title={active ? "active" : "inactive"}
    />
  );
}

function routingVariant(
  r: string,
): "secondary" | "info" | "success" | "warn" | "outline" {
  switch (r) {
    case "persist":
      return "secondary";
    case "ingest":
      return "info";
    case "both":
      return "success";
    default:
      return "outline";
  }
}

function tierVariant(
  t: string,
): "success" | "info" | "warn" | "outline" {
  switch (t) {
    case "high":
      return "success";
    case "medium":
      return "info";
    case "low":
      return "warn";
    default:
      return "outline";
  }
}
